import React from "react";
import { NavLink, Outlet, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { List } from "@phosphor-icons/react";

function NavList({ links, onClick }) {
  return (
    <nav className="space-y-0.5">
      {links.map(({ to, label, Icon, end }) => (
        <NavLink end={end} key={to} to={to}
          onClick={onClick}
          data-testid={`dash-nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
          className={({isActive}) => `mv-nav-item ${isActive ? "active" : ""}`}>
          <Icon size={18} weight="duotone" />
          <span>{label}</span>
        </NavLink>
      ))}
    </nav>
  );
}

export default function DashboardShell({ links = [], title }) {
  const { user, loading } = useAuth();
  const [open, setOpen] = React.useState(false);
  if (loading) return <div className="p-12 text-gray-500">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;

  return (
    <div className="max-w-7xl mx-auto px-4 lg:px-8 py-6 lg:py-8 grid lg:grid-cols-12 gap-6">
      {/* Mobile top bar */}
      <div className="lg:hidden">
        <div className="bg-white rounded-2xl border border-gray-200 p-3 flex items-center justify-between">
          <div className="min-w-0">
            <div className="text-[10px] uppercase font-bold tracking-wider text-gray-500">{title}</div>
            <div className="font-display font-black text-mv-secondary truncate text-sm">{user.name}</div>
          </div>
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button variant="outline" className="rounded-lg shrink-0" data-testid="dash-nav-mobile-toggle">
                <List size={18} weight="bold" className="mr-1.5" /> Menu
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-[85vw] max-w-sm">
              <SheetHeader>
                <SheetTitle>{title}</SheetTitle>
              </SheetHeader>
              <div className="mt-4">
                <NavList links={links} onClick={() => setOpen(false)} />
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>

      {/* Desktop sidebar */}
      <aside className="hidden lg:block lg:col-span-3">
        <div className="bg-white rounded-2xl border border-gray-200 p-3 sticky top-24">
          <div className="px-3 py-2">
            <div className="text-xs uppercase font-bold tracking-wider text-gray-500">{title}</div>
            <div className="font-display font-black text-mv-secondary mt-1 truncate">{user.name}</div>
          </div>
          <div className="mt-2">
            <NavList links={links} />
          </div>
        </div>
      </aside>

      <section className="lg:col-span-9 space-y-6 min-w-0">
        <Outlet />
      </section>
    </div>
  );
}
